import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Plus } from "lucide-react"
import { useNavigate } from "react-router-dom"
import DoctorCard from "@/pages/doctors/components/DoctorCard"
import {
  useFetchDoctors,
  useDeleteDoctor,
} from "@/services/query/doctors.query"
import { AddDoctorDialog } from "@/components/doctors/AddDoctorDialog"
import { EditDoctorDialog } from "@/components/doctors/EditDoctorDialog"
import { useState, useCallback } from "react"
import { useToast } from "@/components/ui/use-toast"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog"

function DoctorsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [pagination, setPagination] = useState({ offset: 0, limit: 9 })
  const [search, setSearch] = useState("")
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedDoctor, setSelectedDoctor] = useState(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

  // Debounce search
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value)
      setPagination((prev) => ({ ...prev, offset: 0 }))
    }, 500),
    []
  )

  const { data, isLoading, isError, error } = useFetchDoctors(
    pagination.offset,
    pagination.limit,
    search
  )

  const deleteDocMutation = useDeleteDoctor()

  const handlePageChange = (newOffset) => {
    setPagination((prev) => ({ ...prev, offset: newOffset }))
  }

  const handleCardClick = (doctorId) => {
    navigate(`/doctors/${doctorId}`)
  }

  const handleDelete = async () => {
    if (!selectedDoctor) return

    try {
      await deleteDocMutation.mutateAsync(selectedDoctor.id)
      toast({
        title: "Success",
        description: "Doctor deleted successfully",
      })
      setDeleteDialogOpen(false)
      setSelectedDoctor(null)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete doctor",
        variant: "destructive",
      })
    }
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="w-full sm:w-auto sm:flex-1 max-w-sm">
          <Input
            type="search"
            placeholder="Search doctors..."
            onChange={(e) => debouncedSearch(e.target.value)}
            className="w-full"
          />
        </div>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Doctor
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {isLoading ? (
          Array(3)
            .fill(0)
            .map((_, idx) => (
              <div key={idx} className="animate-pulse">
                <div className="h-[200px] bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              </div>
            ))
        ) : isError ? (
          <div className="col-span-full p-4 text-center bg-red-50 dark:bg-red-900/10 rounded-lg">
            <p className="text-red-600 dark:text-red-400">
              Error: {error.message}
            </p>
          </div>
        ) : data?.doctors?.length === 0 ? (
          <div className="col-span-full p-8 text-center bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <p className="text-gray-600 dark:text-gray-400">No doctors found</p>
          </div>
        ) : (
          data?.doctors?.map((doctor) => (
            <DoctorCard
              key={doctor.id}
              doctor={doctor}
              onEdit={() => {
                setSelectedDoctor(doctor)
                setIsEditDialogOpen(true)
              }}
              onDelete={() => {
                setSelectedDoctor(doctor)
                setDeleteDialogOpen(true)
              }}
              onClick={() => handleCardClick(doctor.id)}
            />
          ))
        )}
      </div>

      {data && data.pagination.totalPages > 1 && (
        <div className="mt-8 flex justify-center overflow-x-auto">
          <Pagination className="py-2">
            <PaginationContent className="flex-wrap gap-2">
              <PaginationItem className="hidden sm:inline-block">
                <PaginationPrevious
                  onClick={() =>
                    handlePageChange(
                      Math.max(0, pagination.offset - pagination.limit)
                    )
                  }
                  disabled={pagination.offset === 0}
                />
              </PaginationItem>
              {[...Array(data.pagination.totalPages)].map((_, idx) => (
                <PaginationItem key={idx}>
                  <PaginationLink
                    onClick={() => handlePageChange(idx * pagination.limit)}
                    isActive={pagination.offset === idx * pagination.limit}
                    className="min-w-[40px] justify-center"
                  >
                    {idx + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem className="hidden sm:inline-block">
                <PaginationNext
                  onClick={() =>
                    handlePageChange(pagination.offset + pagination.limit)
                  }
                  disabled={
                    pagination.offset + pagination.limit >=
                    data.pagination.total
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <AddDoctorDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />

      <EditDoctorDialog
        doctor={selectedDoctor}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              doctor and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// Debounce utility function
function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export default DoctorsPage
